<template lang="pug">
l-map(
	ref="map"
	:center="mapCenter"
	:zoom="mapZoom"
	@update:center="updateCenter"
	@update:zoom="updateZoom"
	:options="options"
	@click="$emit('click', $event)"
	@contextmenu="onContextMenu"
	@baselayerchange="onBaseLayerChange($event)"
)
	MapPanControl(
		@pan-control-click="handlePan"
		:class="panControlNeedsBuffer ? 'fvu-buffered' : ''"
	)
	l-control-layers(ref="layerControl")
	l-tile-layer(
		v-for="tileProvider in tileProviders"
		:key="tileProvider.name"
		:ref="'tilelayer__' + tileProvider.name"
		:name="tileProvider.name"
		:visible="tileProvider.visible"
		:url="tileProvider.url"
		:options="mergeOptions(tileProvider.options)"
		:attribution="tileProvider.attribution"
		layer-type="base"
		layerType="base"
		@click="$emit('click', $event)"
	)
	l-control-scale(
		position="bottomleft"
		:imperial="true"
		:metric="true"
	)
	slot
</template>

<script>
import _ from "lodash";
import Vue from "vue";
import * as L from "leaflet";
import Component from "vue-class-component";
import MutationTypes from "@/store/mutation-types";
import { LMap, LTileLayer, LControlLayers, LControlScale } from "vue2-leaflet";
import { mapGetters } from "vuex";
import MapPanControl from "@/components/MapPanControl.vue";

const DEFAULT_ZOOM = 7;
const DEFAULT_MAX_ZOOM = 18;
const DEFAULT_LAYERS_CONTROL_POSITION = "topright";

const mapProps = {
	zoom: {
		default: () => null
	},
	options: {
		default: () => ({
			maxZoom: DEFAULT_MAX_ZOOM,
			panControl: true
		})
	},
	tileLayerOptions: {
		default: () => ({
			maxNativeZoom: DEFAULT_MAX_ZOOM,
			maxZoom: DEFAULT_MAX_ZOOM
		})
	},
	center: {
		default: () => null
	},
	positionKey: {
		type: String,
		default: null
	},
	aggressivePositionSaving: {
		type: Boolean,
		default: false
	},
	rememberPosition: {
		type: Boolean,
		default: true
	},
	layersControlPosition: {
		default: () => DEFAULT_LAYERS_CONTROL_POSITION
	},
	panControlNeedsBuffer: {
		type: Boolean,
		default: false
	}
};

@Component({
	props: mapProps,
	components: {
		LMap,
		LTileLayer,
		LControlLayers,
		LControlScale,
		MapPanControl
	},
	computed: {
		...mapGetters(["scrEdit"])
	}
})
class TfmMap extends Vue {
	tileset = null;

	_center = null;
	_zoom = null;

	get tileProviders() {
		return this.$store.state.map.tileProviders;
	}

	get mapObject() {
		try {
			return this.$refs.map.mapObject;
		} catch {
			return null;
		}
	}

	get mapZoom() {
		const propZoom = this.zoom;
		let myZoom = this._zoom;
		const savedData = this.savedPositionData;

		if (propZoom) return propZoom; // prop priority
		if (!myZoom) {
			if (!savedData) return DEFAULT_ZOOM;
			myZoom = savedData.zoom;
			this._zoom = myZoom;
		}
		return myZoom;
	}

	get mapCenter() {
		// dependencies
		const propCenter = this.center;
		let myCenter = this._center;
		const savedData = this.savedPositionData;

		if (propCenter) return propCenter; // prop priority
		if (!myCenter) {
			if (!savedData) return new L.LatLng(43.54456658436357, -83.9959716796875);
			this._center = savedData.center;
			myCenter = this._center;
		}
		return myCenter;
	}

	mounted() {
		/*
		const tileProviders = this.$store.state.map.tileProviders.map(t =>
			Object.assign({}, t)
		);
		*/
		window.addEventListener("resize", _.debounce(this.handleResize, 1));
		const map = this.$refs.map;
		const mapObject = map && map.mapObject;
		this.$emit("map:mounted", { map, mapObject });
		Vue.nextTick(() => {
			Vue.nextTick(() => {
				this.loadPosition();
			});
		});

		/*
			apply margin-left
			to zoom controls, if
			parent component is FVU monitor
		*/
		this.setTopLeftControlBuffer();
	}

	setTopLeftControlBuffer() {
		let zoomControl = document.getElementsByClassName(
			"leaflet-control-zoom"
		)[0];

		if (this.panControlNeedsBuffer) {
			// apply style manually to the zoom control
			zoomControl.classList.add("fvu-buffered");
		}
	}

	beforeDestroy() {
		window.removeEventListener("resize", this.handleResize);
		this.savePosition();
	}

	parentWindowResizeEnd() {
		this.handleResize();
	}

	flyTo(...a) {
		var m = this.$refs.map;
		var mo = m.mapObject;
		if (mo) {
			mo.flyTo(...a);
		}
	}

	/**
	 *
	 * @param {*} directionIndex the direction in
	 * which to create a point
	 * that augments the map center
	 */
	generatePanPoint(directionIndex) {
		const center = this.mapObject.getCenter();

		var longDiff =
			this.mapObject.getBounds()["_northEast"].lng -
			this.mapObject.getBounds()["_southWest"].lng;
		var latDiff =
			this.mapObject.getBounds()["_northEast"].lat -
			this.mapObject.getBounds()["_southWest"].lat;

		let panBumpCoords = {};
		switch (parseInt(directionIndex)) {
			case 0:
				panBumpCoords = {
					x: 0,
					y: latDiff * 0.5
				}; // up
				break;
			case 1:
				panBumpCoords = {
					x: longDiff * 0.5,
					y: 0
				}; // right
				break;
			case 2:
				panBumpCoords = {
					x: 0,
					y: latDiff * -0.5
				}; // down
				break;
			case 3:
				panBumpCoords = {
					x: longDiff * -0.5,
					y: 0
				}; // left
		}

		return new L.LatLng(
			center.lat + panBumpCoords.y,
			center.lng + panBumpCoords.x
		);
	}

	/**
	 * move the map a bit in the direction requested
	 *
	 * @param {number} directionIndex the direction to pan the map by
	 * 0: up
	 * 1: right
	 * 2: down
	 * 3: left
	 */
	handlePan(directionIndex) {
		try {
			const mo = this.mapObject;
			mo.panTo(this.generatePanPoint(directionIndex)); // move the center to there
		} catch (e) {
			console.error("failed to move map", e);
		}
	}

	handleResize() {
		try {
			const mo = this.mapObject;
			if (mo) {
				mo.flyTo(mo.getCenter(), mo.getZoom());
				mo.on("zoomanim", _.debounce(mo._onZoomTransitionEnd, 250));
				mo.invalidateSize();
			}
		} catch (e) {
			console.error("Failed to handle map resize:", e);
		}
	}

	savePosition() {
		try {
			if (!this.rememberPosition || !this.positionKey) return;
			// console.debug("Saving Map Position at Key:", this.positionKey);
			const mo = this.mapObject;
			const c = mo.getCenter();
			const z = mo.getZoom();
			// console.debug("Saving Map Position:", c, z, this.tileset);
			localStorage.setItem(
				`tfmMap.savedPositions.${this.positionKey}`,
				JSON.stringify({ center: c, zoom: z, tileset: this.tileset })
			);
		} catch (e) {
			console.error("Failed to save map position:", e);
		}
	}

	get savedPositionData() {
		try {
			const json = localStorage.getItem(
				`tfmMap.savedPositions.${this.positionKey}`
			);
			if (!json) return null;
			const data = JSON.parse(json);
			return data;
		} catch {
			return null;
		}
	}

	loadPosition() {
		try {
			if (!this.rememberPosition || !this.positionKey) return;
			const data = this.savedPositionData;
			// console.debug("Loading Map Position:", data);
			this.updateCenter(data.center);
			this.updateZoom(data.zoom);
			Vue.nextTick(() => {
				const mo = this.mapObject;
				if (mo) {
					mo.setZoom(data.zoom);
					mo.panTo(data.center);
				}
				this.$store.commit(
					MutationTypes.SET_TILE_PROVIDER_VISIBILITY,
					data.tileset
				);
			});
		} catch (e) {
			console.error("Failed to load map position:", e);
		}
	}

	updateCenter(c) {
		this._center = c;
		this.$emit("update:center", c);
		if (this.aggressivePositionSaving) this.savePosition();
	}

	updateZoom(z) {
		this._zoom = z;
		this.$emit("update:zoom", z);
		if (this.aggressivePositionSaving) this.savePosition();
	}

	mergeOptions(options) {
		return Object.assign({}, this.tileLayerOptions, options);
	}

	onBaseLayerChange(e) {
		const isFirst = !this.tileset;
		this.tileset = e.name;
		this.$store.commit(MutationTypes.SET_TILE_PROVIDER_VISIBILITY, e.name);
		// this.$forceUpdate();
		if (!isFirst) this.savePosition();
		Vue.nextTick(() => {
			this.$refs.layerControl.mapObject._layers = [];
			for (const tpi in this.tileProviders) {
				const tp = this.tileProviders[tpi];
				if (!tp || !tp.name || !this.$refs["tilelayer__" + tp.name]) continue;
				const layermo = this.$refs["tilelayer__" + tp.name][0].mapObject;
				this.$refs.layerControl.mapObject.addBaseLayer(layermo, tp.name);
			}
		});
	}

	onContextMenu(ev) {
		if (this.scrEdit) this.$emit("contextmenu", ev);
	}
}

export default TfmMap;
export { mapProps };
</script>

<style lang="stylus">
@import "~@/../node_modules/leaflet/dist/leaflet.css";

.vue2leaflet-map{
	height 100%;
	width 100%;
}

	.leaflet-bottom, .leaflet-top{
		z-index 990;
	}

.leaflet-left .leaflet-control{
	margin-left 1em;
}

.leaflet-control-layers-base > label{
	text-align left;
	text-transform none;
	font-weight 400;
}

@media screen and (max-width: 600px)
	.leaflet-left .leaflet-control{
		margin-left 1.2rem;
	}

.leaflet-control-attribution{
	z-index -1;
	margin-bottom 0;
	margin-left 0;
}

.leaflet-control-container .leaflet-routing-container-hide .leaflet-routing-alt{
	display none;
}

.leaflet-control-container .leaflet-routing-container-hide .leaflet-routing-geocoders{
	display none;
}

.leaflet-control-container div.leaflet-top.leaflet-left{
	display flex;
	flex-direction column;
	z-index 599;
}

.leaflet-bar{
	box-shadow: none;
}

.leaflet-control-pan{
	order:1;
	margin-bottom:10px;
}

.leaflet-control-zoom{
	order:2;
	padding-left: 30px;
	border:none !important;
}

	.leaflet-control-zoom a{
		width: 30px;
		height: 30px;
		background-color: #ececeb;
		border: 5px solid #c5c3bf !important;
		border-radius: 10px !important;
		padding-bottom:25px;
		line-height:25px !important;
	}

	.leaflet-control-zoom a:hover{
		background-color: #09f;
		width: 30px;
		height: 30px;
	}

.fvu-buffered {
	margin-left: 2pc !important;
}
</style>
